"use client"

import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { useEffect, useState } from "react"
import { FaHeart } from "react-icons/fa"

import { cn } from "../../lib/cn"
import { ease } from "../../lib/motion"

/**
 * PromotionBadge - A subtle pill badge that appears in the bottom-right corner
 *
 * **Behavior:**
 * - Slides in from the right on mount
 * - Automatically slides out and disappears after 60 seconds
 * - Respects prefers-reduced-motion
 *
 * **Usage:**
 * Simply drop the component anywhere in your app - no props needed.
 *
 * @example
 * ```tsx
 * import { PromotionBadge } from '@mdxui/neo'
 *
 * function App() {
 *   return (
 *     <>
 *       <main>...</main>
 *       <PromotionBadge />
 *     </>
 *   )
 * }
 * ```
 */
const PromotionBadge = ({ className }: { className?: string }) => {
  const [isVisible, setIsVisible] = useState(false)
  const prefersReducedMotion = useReducedMotion()

  // Pop in at 10 seconds, leave at 15 seconds
  useEffect(() => {
    const showTimer = setTimeout(() => {
      setIsVisible(true)
    }, 10000) // Show at 10 seconds

    const hideTimer = setTimeout(() => {
      setIsVisible(false)
    }, 15000) // Hide at 15 seconds

    return () => {
      clearTimeout(showTimer)
      clearTimeout(hideTimer)
    }
  }, [])

  const slideVariants = {
    hidden: {
      x: 100,
      opacity: 0,
    },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease,
      },
    },
    exit: {
      x: 100,
      opacity: 0,
      transition: {
        duration: 0.4,
        ease,
      },
    },
  }

  const reducedMotionVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, transition: { duration: 0.3 } },
  }

  const variants = prefersReducedMotion ? reducedMotionVariants : slideVariants

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={cn(
            "fixed bottom-6 right-4 z-50",
            "flex items-center gap-1.5",
            "rounded-full border border-border bg-background px-3 py-1.5",
            "text-sm text-muted-foreground",
            "shadow-sm",
            className,
          )}
          variants={variants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <span>With</span>
          <FaHeart className="size-3 text-red-500" />
          <span>by .do</span>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export { PromotionBadge }
